/**
 * useVaultKeyboard - Keyboard navigation hook for The Vault
 * USER-CON-001 UI-004: Power user keyboard bindings
 *
 * Bindings:
 * - J/K or ↓/↑: Navigate items
 * - H/L or ←/→: Navigate views
 * - E: Edit selected item
 * - S: Add to sell queue
 * - B: Assign to box
 * - G: Toggle wanted (gap/wish list)
 * - R: Toggle read status
 * - D/Delete: Delete selected
 * - /: Focus search
 * - Cmd/Ctrl+K: Open command palette
 * - Escape: Close panels/deselect
 * - 1-4: Switch views (Grid, List, Run, Matrix)
 * - N: New item
 * - I: Import
 */
import { useEffect, useCallback } from 'react';

export default function useVaultKeyboard({
  items = [],
  activeIndex = -1,
  selectedItems = [],
  onNavigate,
  onSelect,
  onAction,
  onViewChange,
  onOpenCommandPalette,
  onFocusSearch,
  onClose,
  enabled = true,
}) {
  const handleKeyDown = useCallback((e) => {
    // Skip if typing in an input
    const target = e.target;
    const isInput = target.tagName === 'INPUT' ||
                    target.tagName === 'TEXTAREA' ||
                    target.isContentEditable;

    // Allow Cmd+K and Escape even in inputs
    const isMeta = e.metaKey || e.ctrlKey;

    // Command palette: Cmd/Ctrl+K
    if (isMeta && e.key === 'k') {
      e.preventDefault();
      onOpenCommandPalette?.();
      return;
    }

    // Escape always works
    if (e.key === 'Escape') {
      e.preventDefault();
      onClose?.();
      return;
    }

    // Skip other shortcuts if in input
    if (isInput) return;

    switch (e.key) {
      // Navigation - Down
      case 'j':
      case 'ArrowDown':
        e.preventDefault();
        if (items.length > 0) {
          const nextIndex = Math.min(activeIndex + 1, items.length - 1);
          onNavigate?.(nextIndex);
        }
        break;

      // Navigation - Up
      case 'k':
      case 'ArrowUp':
        e.preventDefault();
        if (items.length > 0) {
          const prevIndex = Math.max(activeIndex - 1, 0);
          onNavigate?.(prevIndex);
        }
        break;

      // View switching with H/L (previous/next)
      case 'h':
      case 'ArrowLeft':
        if (!e.shiftKey) {
          e.preventDefault();
          onViewChange?.('prev');
        }
        break;

      case 'l':
      case 'ArrowRight':
        if (!e.shiftKey) {
          e.preventDefault();
          onViewChange?.('next');
        }
        break;

      // View switching with numbers
      case '1':
        e.preventDefault();
        onViewChange?.('grid');
        break;

      case '2':
        e.preventDefault();
        onViewChange?.('list');
        break;

      case '3':
        e.preventDefault();
        onViewChange?.('run');
        break;

      case '4':
        e.preventDefault();
        onViewChange?.('matrix');
        break;

      // Edit selected
      case 'e':
      case 'E':
        e.preventDefault();
        if (activeIndex >= 0 && items[activeIndex]) {
          onAction?.('edit', items[activeIndex]);
        }
        break;

      // Add to sell queue
      case 's':
        if (!isMeta) {
          e.preventDefault();
          if (selectedItems.length > 0) {
            onAction?.('sell-queue', selectedItems);
          } else if (activeIndex >= 0 && items[activeIndex]) {
            onAction?.('sell-queue', [items[activeIndex].id]);
          }
        }
        break;

      // Assign to box
      case 'b':
      case 'B':
        e.preventDefault();
        if (selectedItems.length > 0) {
          onAction?.('assign-box', selectedItems);
        } else if (activeIndex >= 0 && items[activeIndex]) {
          onAction?.('assign-box', [items[activeIndex].id]);
        }
        break;

      // Toggle wanted/gap
      case 'g':
      case 'G':
        e.preventDefault();
        if (activeIndex >= 0 && items[activeIndex]) {
          onAction?.('toggle-wanted', items[activeIndex]);
        }
        break;

      // Toggle read status
      case 'r':
        if (!isMeta) {
          e.preventDefault();
          if (activeIndex >= 0 && items[activeIndex]) {
            onAction?.('toggle-read', items[activeIndex]);
          }
        }
        break;

      // Delete
      case 'd':
      case 'D':
      case 'Delete':
      case 'Backspace':
        if (e.key === 'Delete' || e.key === 'Backspace' || (e.key === 'd' && !isMeta)) {
          e.preventDefault();
          if (selectedItems.length > 0) {
            onAction?.('delete', selectedItems);
          } else if (activeIndex >= 0 && items[activeIndex]) {
            onAction?.('delete', [items[activeIndex].id]);
          }
        }
        break;

      // Focus search
      case '/':
        e.preventDefault();
        onFocusSearch?.();
        break;

      // New item
      case 'n':
      case 'N':
        if (!isMeta) {
          e.preventDefault();
          onAction?.('new-item');
        }
        break;

      // Import
      case 'i':
      case 'I':
        if (!isMeta) {
          e.preventDefault();
          onAction?.('import');
        }
        break;

      // Select all (with Cmd/Ctrl)
      case 'a':
      case 'A':
        if (isMeta) {
          e.preventDefault();
          onSelect?.('all');
        }
        break;

      // Toggle selection with Space
      case ' ':
        e.preventDefault();
        if (activeIndex >= 0 && items[activeIndex]) {
          onSelect?.('toggle', items[activeIndex].id);
        }
        break;

      // Enter to open detail
      case 'Enter':
        e.preventDefault();
        if (activeIndex >= 0 && items[activeIndex]) {
          onAction?.('open-detail', items[activeIndex]);
        }
        break;

      default:
        break;
    }
  }, [
    items,
    activeIndex,
    selectedItems,
    onNavigate,
    onSelect,
    onAction,
    onViewChange,
    onOpenCommandPalette,
    onFocusSearch,
    onClose,
  ]);

  useEffect(() => {
    if (!enabled) return;

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [enabled, handleKeyDown]);

  return {
    // Expose for manual triggering if needed
    handleKeyDown,
  };
}
